/**
 * Domain types for the Product Identifier module (BM-9580).
 *
 * Mirrors the backend shapes in `server/domain/product-identifier/types.ts`. This module has no
 * imports from the legacy `ProductIdentifier/` folder so that folder can be deleted outright.
 */

/** The four combination drivers, in canonical column order. */
export const AXIS_KEYS = ['colourwayId', 'sizeId', 'lengthId', 'additionalVariableId'] as const;

export type AxisKey = (typeof AXIS_KEYS)[number];

/** One combination dimension. An axis with no values does not participate. */
export interface Axis {
  key: AxisKey;
  values: ReadonlyArray<string>;
}

/** A value per participating axis. */
export type Combination = Partial<Record<AxisKey, string>>;

/** A brand-configured column value on a row. */
export interface CustomCell {
  identifierId: string;
  value: string;
}

/**
 * A barcode drawn from a Retail Identifier library, on a row (BM-9855).
 *
 * The pool is claimed on save, not on the pull click, so a number the user can see is not yet
 * owned by this product. `isPending` is what says so: a previewed cell and a claimed one are
 * otherwise identical. It also keeps a GTIN-14 from being generated off a number the save may
 * still substitute.
 *
 * `isPending` is client-only. The Save button strips it, so the server never receives the key.
 *
 * `isManuallyAddedBarcode` marks a number typed into the Range Plan sheet rather than drawn from a
 * pool. It has no barcode id, yet counts as saved. The server decides it; the client only carries it.
 */
export interface RetailCell {
  retailLibraryId: string;
  retailBarcodeId: string | null;
  retailBarcodeNumber: string;
  isPending?: boolean;
  isManuallyAddedBarcode?: boolean;
}

/**
 * One product-identifier row. `sizeRangeId` replaces the legacy grading-group `tabId`, and
 * `_id` is the server-assigned subdocument id used as the grid row key.
 *
 * The index signature is required by `BombiixRow`, which the grid constrains its row generic to.
 * It also lets a column read its value by dynamic field name without a cast.
 */
export interface IdentifierRow extends Combination {
  [key: string]: unknown;
  _id?: string;
  sizeRangeId?: string | null;
  /**
   * The `AdditionalVariables` document that `additionalVariableId` is an option of.
   *
   * Deliberately NOT in `AXIS_KEYS`: it holds the same value on every row of a product,
   * so it distinguishes nothing, and `isSameCombination` must keep ignoring it or every
   * persisted row would stop matching its own combination.
   */
  additionalVariableMasterId?: string | null;
  productNumber?: string;
  combination?: string;
  customCells: CustomCell[];
  retailCells?: RetailCell[];
  lastCounter?: Record<string, string>;
  factorCells?: FactorCell[];
}

/**
 * What a Retail Identifier column's pull button needs: which pool to draw from, and how
 * many numbers are left in it.
 */
export interface ResolvedRetailColumn {
  libraryId: string;
  barcodeType: string;
  batchNumber: string;
  available: number;
}

/** A column definition resolved for this product, already filtered for visibility. */
export interface ResolvedColumn {
  identifierId: string;
  name: string;
  refCode?: string;
  recordType: 'textBox' | 'dropdown' | 'date';
  seqNumber: number;
  isSystem: boolean;
  /** Row field this column binds to, for system columns. */
  field?: string;
  isAutoGenerated: boolean;
  /** Present only on Retail Identifier columns (BM-9855). */
  retail?: ResolvedRetailColumn;
  /** Present only on a GTIN-14 group column: a factor indicator or a derived Inner / Master GTIN-14. */
  factor?: ResolvedFactorColumn;
}

/**
 * One previewed combination-to-barcode pairing returned by the pull endpoint.
 *
 * Every axis is optional AND explicitly `undefined`-able: an axis the product does not
 * use is omitted from the JSON entirely, and `isSameCombination` normalises `''`, `null`
 * and `undefined` to the same thing when matching.
 */
export interface RetailPreviewEntry {
  colourwayId?: string | undefined;
  sizeRangeId?: string | null | undefined;
  sizeId?: string | undefined;
  lengthId?: string | undefined;
  additionalVariableId?: string | undefined;
  barcodeId: string;
  barcodeNumber: string;
}

/** The payload returned by the retail pull preview endpoint. */
export interface RetailPreviewResult {
  libraryId: string;
  entries: RetailPreviewEntry[];
  alreadyFilled: number;
  shortfall: number;
  available: number;
}

/** A size-range tab. */
export interface ResolvedTab {
  sizeRangeId: string;
  name: string;
}

/**
 * id -> display name for every reference the grid renders as text, keyed by row field.
 * System columns are read-only strings, so the client needs labels rather than raw ObjectIds.
 */
export interface ViewLabels {
  colourwayId: Record<string, string>;
  sizeId: Record<string, string>;
  lengthId: Record<string, string>;
  additionalVariableId: Record<string, string>;
  sizeRangeId: Record<string, string>;
}

/**
 * Product context for the page header — thumbnail, number and description.
 *
 * Optional so a response predating this field renders the bare title instead of throwing.
 */
export interface ProductHeader {
  number: string;
  description: string;
  image: string;
}

/** The full grid payload returned by GET. */
export interface ProductIdentifierView {
  devOrderId: string;
  identifierType: 'auto' | 'manual';
  columns: ResolvedColumn[];
  tabs: ResolvedTab[];
  rows: IdentifierRow[];
  labels: ViewLabels;
  isArchived: boolean;
  product?: ProductHeader;
}

/** The payload returned by the generate endpoint. */
export interface GenerateResult {
  rows: IdentifierRow[];
  latestCounterValues: Record<string, Record<string, string>>;
}

/** Options a dropdown cell can offer. */
export interface SelectOption {
  label: string;
  value: string;
}

/** Reducer state for the module. */
export interface ProductIdentifierState {
  loading: boolean;
  saving: boolean;
  error: string | null;
  view: ProductIdentifierView | null;
  rows: IdentifierRow[];
  dirty: boolean;
  retailRefusals?: RetailRefusal[];
}

export type ProductIdentifierAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; view: ProductIdentifierView }
  | { type: 'LOAD_FAILURE'; error: string }
  | { type: 'SET_ROWS'; rows: IdentifierRow[]; dirty?: boolean }
  | { type: 'UPDATE_CELL'; rowId: string; field: string; value: string }
  | { type: 'SET_RETAIL_CELLS'; libraryId: string; entries: RetailPreviewEntry[] }
  | { type: 'ADD_ROW'; row: IdentifierRow }
  | { type: 'REMOVE_ROW'; rowId: string }
  | { type: 'SAVE_START' }
  | { type: 'SAVE_SETTLED'; error?: string }
  | { type: 'RESET' }
  | { type: 'GENERATE_GTIN14'; column: ResolvedFactorColumn }
  | { type: 'SET_RETAIL_REFUSALS'; refusals: RetailRefusal[] };

/** Which values one axis takes across a product's saved identifier rows. */
export interface AxisUsage {
  /** Distinct non-empty ids the rows use. */
  ids: string[];
  /** True when at least one row carries no value on this axis. */
  hasEmpty: boolean;
}

/**
 * Per-axis summary of a product's saved identifier rows — what the Product Information edit page
 * needs to tell whether a change to the product's selections would delete any of them.
 */
export interface IdentifierAxisUsage {
  /** How many rows are stored. Zero means an edit can delete nothing. */
  rowCount: number;
  axes: Record<AxisKey, AxisUsage>;
}

/**
 * A packaging (GTIN-14) value on a row, one per `FactorIndicatorMaster` record: the factor
 * indicator digit typed in the grid and the Inner or Master GTIN-14 generated from it.
 * `retailBarcodeId` is set by the server once the code is recorded on its packaging card.
 */
export interface FactorCell {
  factorIndicatorId: string;
  value: string;
  retailLibraryId: string | null;
  retailBarcodeId: string | null;
  retailBarcodeNumber: string;
}

/** What a GTIN-14 group column carries: which factor cell it edits or generates, and from what. */
export interface ResolvedFactorColumn {
  factorIndicatorId: string;
  kind: 'indicator' | 'gtin14';
  level: 'inner' | 'master';
  /** `GTIN-12` or `GTIN-13`: the base the GTIN-14 is derived from. */
  baseBarcodeType: string;
  /** The base column's library, whose saved barcode is the derivation's input. */
  baseLibraryId: string;
  /** The Inner or Master card the code is recorded on. */
  packagingLibraryId: string;
}

/** Why a typed barcode cannot be saved: found on the page, or refused by the server (BM-10195). */
export type RetailRefusalReason =
  | 'TOO_SHORT'
  | 'NOT_NUMERIC'
  | 'ALL_ZEROS'
  | 'WRONG_LENGTH'
  | 'DUPLICATE_IN_GRID'
  | 'IN_RETAIL_LIBRARY'
  | 'HELD_BY_ANOTHER_PRODUCT';

/** One typed barcode that cannot be saved, with the combination and card it was typed into. */
export interface RetailRefusal {
  combination: string;
  sizeRangeId?: string | null;
  colourwayId?: string | null;
  sizeId?: string | null;
  lengthId?: string | null;
  additionalVariableId?: string | null;
  retailLibraryId: string;
  barcodeNumber: string;
  reason: RetailRefusalReason;
}
